import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import StatementExportModal from "@/components/StatementExportModal";
import { useMedia } from "@/lib/useMedia";
import {
  CaretDownIcon, CaretUpIcon, DotsThreeVerticalIcon, FilePdfIcon, ReceiptIcon, ArrowsLeftRightIcon, TrayArrowDownIcon,
  WarningIcon, ClockIcon, CheckCircleIcon, LockSimpleIcon, XIcon, PaperclipIcon, FolderIcon,
} from "@phosphor-icons/react";

const disponible = (f) => Math.max(0, Number(f.saldo_pendiente || 0) - Number(f.saldo_nc || 0));
const enNc = (f) => Math.min(Number(f.saldo_pendiente || 0), Number(f.saldo_nc || 0));

export function useGestor() {
  const { user } = useAuth();
  const eff = user?.effective || {};
  const admin = Boolean(eff.is_admin);
  return { admin, gestor: admin || (eff.roles || []).includes("delegado") };
}

/** Estado de la factura con texto e icono (no solo color). */
export function EstadoFactura({ f }) {
  if (f.bloqueo_saldo) {
    return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border border-agro-yellow/40 text-agro-yellow bg-agro-yellow/10">
      <LockSimpleIcon size={11} /> {f.bloqueo_saldo.tipo === "nc" ? "Traslado a NC en trámite" : "Cambio de proyecto en trámite"}</span>;
  }
  if (f.vencida) return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border border-red-500/40 text-red-300 bg-red-500/10"><WarningIcon size={11} /> Vencida</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border border-border text-text-secondary"><ClockIcon size={11} /> Por vencer</span>;
}

function Menu({ acciones }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("pointerdown", fuera);
    return () => document.removeEventListener("pointerdown", fuera);
  }, [abierto]);
  if (!acciones.length) return null;
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={(e) => { e.stopPropagation(); setAbierto((v) => !v); }} aria-expanded={abierto} aria-haspopup="menu"
        className="min-h-[44px] px-3 border border-border rounded-md text-xs font-semibold flex items-center gap-1.5 hover:border-agro-green/50" data-testid="factura-acciones">
        <DotsThreeVerticalIcon size={16} weight="bold" /> Acciones</button>
      {abierto && (
        <>
          {/* teléfono: hoja inferior con fondo; escritorio: menú desplegable */}
          <div className="fixed inset-0 z-40 bg-black/50 sm:hidden" onPointerDown={() => setAbierto(false)} aria-hidden="true" />
          <div role="menu" className="fixed inset-x-0 bottom-0 z-50 bg-card border-t border-border rounded-t-2xl shadow-2xl pt-2 pb-[max(12px,env(safe-area-inset-bottom))]
            sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-1 sm:w-64 sm:rounded-lg sm:border sm:pb-1 sm:pt-1">
          <div className="sm:hidden mx-auto mb-1 h-1 w-10 rounded-full bg-border" aria-hidden="true" />
          {acciones.map((a) => (
            <button key={a.l} type="button" role="menuitem" disabled={a.disabled} title={a.disabled || ""} onClick={(e) => { e.stopPropagation(); setAbierto(false); a.on(); }}
              data-testid={a.testid} className="w-full min-h-[48px] sm:min-h-[44px] px-4 sm:px-3 text-left text-sm flex items-center gap-2.5 hover:bg-white/[0.05] disabled:opacity-50 disabled:cursor-not-allowed">
              <a.i size={16} className="shrink-0 text-agro-green" aria-hidden="true" /> <span>{a.l}{a.disabled && <span className="block text-[10px] text-text-muted">{a.disabled}</span>}</span>
            </button>
          ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Tarjeta de factura (teléfono) y fila (escritorio) con lo esencial y el detalle desplegable. */
function Factura({ f, acciones }) {
  const [detalle, setDetalle] = useState(false);
  const nc = enNc(f);
  const cuenta = f.proyecto_codigo ? `${f.proyecto_codigo}${f.proyecto_nombre ? ` · ${f.proyecto_nombre}` : ""}` : "Cuenta principal";
  return (
    <li className={`border border-border/60 rounded-lg p-3 ${f.vencida ? "bg-red-500/[0.04]" : "bg-app/40"}`} data-testid={`receivable-row-${f.numero}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-sm text-agro-green">
            {f.documento_tipo === "nota_debito" && <span className="font-sans text-[10px] uppercase mr-1 px-1.5 py-0.5 rounded border border-agro-yellow/40 text-agro-yellow">ND</span>}{f.numero}</div>
          <div className="text-xs text-text-secondary flex items-center gap-1 mt-0.5"><FolderIcon size={12} aria-hidden="true" /> {cuenta}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase text-text-muted">Saldo</div>
          <div className="font-semibold text-agro-yellow tabular-nums">{fmtL(disponible(f))}</div>
          {nc > 0.004 && <div className="text-[11px] text-text-secondary">+ {fmtL(nc)} en NC</div>}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2"><EstadoFactura f={f} />
          <span className="text-xs text-text-muted">Vence {f.fecha_vencimiento ? fmtDate(f.fecha_vencimiento) : "—"}</span></div>
        <div className="flex items-center gap-2 ml-auto">
          <button type="button" onClick={() => setDetalle((v) => !v)} aria-expanded={detalle}
            className="min-h-[44px] px-2 text-xs text-text-secondary flex items-center gap-1">{detalle ? <CaretUpIcon size={14} /> : <CaretDownIcon size={14} />} Detalle</button>
          <Menu acciones={acciones} />
        </div>
      </div>
      {detalle && (
        <dl className="mt-2 pt-2 border-t border-border/40 grid grid-cols-2 gap-x-3 gap-y-1 text-xs" data-testid="factura-detalle">
          <dt className="text-text-muted">Emisión</dt><dd>{fmtDate(f.fecha)}</dd>
          <dt className="text-text-muted">Total</dt><dd className="tabular-nums">{fmtL(f.total)}</dd>
          <dt className="text-text-muted">Saldo de la factura</dt><dd className="tabular-nums">{fmtL(f.saldo_pendiente)}</dd>
          {nc > 0.004 && <><dt className="text-text-muted">En subcuenta NC</dt><dd className="tabular-nums">{fmtL(nc)}</dd></>}
          {f.subcuenta_codigo && <><dt className="text-text-muted">Subcuenta</dt><dd className="font-mono">{f.subcuenta_codigo}</dd></>}
          {f.memo && <><dt className="text-text-muted col-span-2">Memo / detalle</dt><dd className="col-span-2 text-text-secondary whitespace-pre-wrap">{f.memo}</dd></>}
        </dl>
      )}
    </li>
  );
}

/** Cliente con su cartera: resumen (ordinaria / NC / vencido), acciones y facturas. */
export default function CarteraCliente({ g, onPay, onCambio }) {
  const { gestor } = useGestor();
  const [abierto, setAbierto] = useState(false);
  const [exportar, setExportar] = useState(false);
  const [modal, setModal] = useState(null);          // {tipo: "proyecto"|"nc", f}
  const escritorio = useMedia("(min-width: 1024px)");
  const totalNc = g.facturas.reduce((s, f) => s + enNc(f), 0);
  const ordinaria = g.total - totalNc;
  const acciones = (f) => {
    const out = [];
    if (onPay) out.push({ l: "Recibir pago", i: ReceiptIcon, on: () => onPay(f), testid: `accion-pago-${f.numero}` });
    if (gestor && f.documento_tipo !== "nota_debito") {
      const bloq = f.bloqueo_saldo ? "Tiene una solicitud en trámite" : "";
      out.push({ l: "Cambiar proyecto o subcuenta", i: ArrowsLeftRightIcon, on: () => setModal({ tipo: "proyecto", f }), disabled: bloq, testid: `accion-proyecto-${f.numero}` });
      out.push({ l: "Mover a subcuenta NC", i: TrayArrowDownIcon, on: () => setModal({ tipo: "nc", f }),
        disabled: bloq || (disponible(f) <= 0.004 ? "Sin saldo disponible" : ""), testid: `accion-nc-${f.numero}` });
    }
    return out;
  };
  const ventanas = (
    <>
      {exportar && <StatementExportModal clienteId={g.cliente_id} clienteNombre={g.cliente_nombre} clienteCodigo={g.cliente_codigo} onClose={() => setExportar(false)} />}
      {modal?.tipo === "proyecto" && <CambioProyectoModal factura={modal.f} onClose={() => setModal(null)} onDone={() => { setModal(null); onCambio && onCambio(); }} />}
      {modal?.tipo === "nc" && <MoverNcModal factura={modal.f} onClose={() => setModal(null)} onDone={() => { setModal(null); onCambio && onCambio(); }} />}
    </>
  );
  // Computadora: lista (tabla) con las acciones como íconos; teléfono y tableta: tarjetas con menú «Acciones»
  if (escritorio) {
    return (
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid={`group-${g.cliente_id}`}>
        <div className="flex items-center justify-between gap-4 p-4 cursor-pointer hover:bg-white/[0.02]" onClick={() => setAbierto((v) => !v)}>
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" className="text-text-muted min-h-[32px] min-w-[32px] flex items-center justify-center" aria-expanded={abierto}
              aria-label={abierto ? "Ocultar facturas" : "Ver facturas"}>{abierto ? <CaretUpIcon size={16} /> : <CaretDownIcon size={16} />}</button>
            <div className="min-w-0">
              <div className="font-medium text-text-primary truncate">
                {g.cliente_codigo && <span className="font-mono text-xs text-agro-green mr-2" data-testid={`codigo-${g.cliente_id}`}>{g.cliente_codigo}</span>}
                {g.cliente_nombre}
              </div>
              <div className="text-[11px] text-text-muted">{g.facturas.length} {g.facturas.length === 1 ? "documento pendiente" : "documentos pendientes"}</div>
            </div>
          </div>
          <div className="flex items-center gap-5 flex-shrink-0">
            {g.vencido > 0 && <span className="text-xs text-red-400">Vencido: <strong>{fmtL(g.vencido)}</strong></span>}
            {totalNc > 0.004 && <span className="text-xs text-text-secondary">En NC: <strong>{fmtL(totalNc)}</strong></span>}
            <div className="text-right">
              <div className="text-[10px] uppercase text-text-muted">Total saldo</div>
              <div className="font-heading text-lg text-agro-yellow">{fmtL(ordinaria)}</div>
            </div>
            <button type="button" onClick={(e) => { e.stopPropagation(); setExportar(true); }} data-testid={`statement-pdf-${g.cliente_id}`}
              title="Descarga el estado de cuenta del cliente (mismo formato que en su ficha)"
              className="border border-blue-500/50 hover:bg-blue-500/10 text-blue-300 text-xs font-semibold rounded px-3 py-2 flex items-center gap-1.5">
              <FilePdfIcon size={14} weight="bold" /> Descargar estado de cuenta PDF</button>
            {onPay && <button type="button" onClick={(e) => { e.stopPropagation(); onPay(null); }} data-testid={`pay-client-${g.cliente_id}`}
              className="bg-agro-green hover:bg-agro-green-hover text-black text-xs font-semibold rounded px-3 py-2 flex items-center gap-1.5">
              <ReceiptIcon size={14} weight="bold" /> Aplicar cobro</button>}
          </div>
        </div>
        {abierto && (
          <div className="border-t border-border/60 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
                <tr className="text-left">
                  <th className="p-3 pl-12">Factura</th><th className="p-3">Proyecto</th><th className="p-3">Fecha</th><th className="p-3">Vence</th>
                  <th className="p-3">Estado</th><th className="p-3">Memo / Detalle</th><th className="p-3 text-right">Total</th>
                  <th className="p-3 text-right">Saldo</th><th className="p-3 pr-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {g.facturas.map((f) => {
                  const nc = enNc(f);
                  return (
                    <tr key={f.id} className={`border-t border-border/40 ${f.vencida ? "bg-red-500/[0.03]" : ""}`} data-testid={`receivable-row-${f.numero}`}>
                      <td className="p-3 pl-12 font-mono text-xs text-agro-green whitespace-nowrap">
                        {f.documento_tipo === "nota_debito" && <span className="font-sans text-[9px] uppercase mr-1 px-1.5 py-0.5 rounded border border-agro-yellow/40 text-agro-yellow">ND</span>}
                        {f.numero}</td>
                      <td className="p-3 text-xs text-text-secondary">{f.proyecto_codigo || "Cuenta principal"}</td>
                      <td className="p-3 text-xs text-text-secondary whitespace-nowrap">{fmtDate(f.fecha)}</td>
                      <td className={`p-3 text-xs whitespace-nowrap ${f.vencida ? "text-red-400 font-medium" : "text-text-secondary"}`}>{f.fecha_vencimiento ? fmtDate(f.fecha_vencimiento) : "—"}</td>
                      <td className="p-3"><EstadoFactura f={f} /></td>
                      <td className="p-3 text-xs text-text-secondary max-w-[260px]" data-testid={`receivable-memo-${f.numero}`}>
                        <div className="truncate" title={f.memo || ""}>{f.memo || "—"}</div></td>
                      <td className="p-3 text-right text-xs whitespace-nowrap">{fmtL(f.total)}</td>
                      <td className="p-3 text-right whitespace-nowrap"><div className="text-sm font-medium text-agro-yellow">{fmtL(disponible(f))}</div>
                        {nc > 0.004 && <div className="text-[10px] text-text-muted">+ {fmtL(nc)} en NC</div>}</td>
                      <td className="p-3 pr-4">
                        <div className="flex justify-end gap-1">
                          {acciones(f).map((a) => (
                            <button key={a.l} type="button" onClick={a.on} disabled={Boolean(a.disabled)} title={a.disabled ? `${a.l}: ${a.disabled}` : a.l} aria-label={a.l}
                              data-testid={a.testid} className="p-1.5 rounded text-text-muted hover:text-agro-green hover:bg-white/[0.05] disabled:opacity-30 disabled:cursor-not-allowed">
                              <a.i size={17} /></button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {ventanas}
      </div>
    );
  }
  return (
    <div className="bg-card border border-border/60 rounded-xl" data-testid={`group-${g.cliente_id}`}>
      <button type="button" onClick={() => setAbierto((v) => !v)} aria-expanded={abierto} className="w-full text-left p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="font-semibold text-text-primary">{g.cliente_nombre}</div>
          <div className="text-xs text-text-muted mt-0.5">
            {g.cliente_codigo && <span className="font-mono text-agro-green mr-2" data-testid={`codigo-${g.cliente_id}`}>{g.cliente_codigo}</span>}
            {g.facturas.length} {g.facturas.length === 1 ? "documento pendiente" : "documentos pendientes"}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 sm:flex sm:gap-6 text-right">
          <div><div className="text-[10px] uppercase text-text-muted">Cobranza</div><div className="font-heading text-base sm:text-lg text-agro-yellow tabular-nums">{fmtL(ordinaria)}</div></div>
          <div><div className="text-[10px] uppercase text-text-muted">Vencido</div><div className="text-sm font-semibold text-red-300 tabular-nums">{fmtL(g.vencido)}</div></div>
          <div><div className="text-[10px] uppercase text-text-muted">En NC</div><div className="text-sm font-semibold text-text-secondary tabular-nums">{fmtL(totalNc)}</div></div>
        </div>
      </button>
      <div className="px-4 pb-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => setAbierto((v) => !v)} className="min-h-[44px] px-3 border border-border rounded-md text-xs font-semibold flex items-center gap-1.5">
          {abierto ? <CaretUpIcon size={14} /> : <CaretDownIcon size={14} />} {abierto ? "Ocultar facturas" : "Ver facturas"}</button>
        <button type="button" onClick={() => setExportar(true)} data-testid={`statement-pdf-${g.cliente_id}`}
          className="min-h-[44px] px-3 border border-blue-500/50 text-blue-300 rounded-md text-xs font-semibold flex items-center gap-1.5"><FilePdfIcon size={14} /> Estado de cuenta</button>
        {onPay && <button type="button" onClick={() => onPay(null)} data-testid={`pay-client-${g.cliente_id}`}
          className="min-h-[44px] px-3 bg-agro-green hover:bg-agro-green-hover text-black rounded-md text-xs font-semibold flex items-center gap-1.5"><ReceiptIcon size={14} weight="bold" /> Aplicar cobro</button>}
      </div>
      {abierto && (
        <ul className="px-4 pb-4 grid grid-cols-1 xl:grid-cols-2 gap-2">
          {g.facturas.map((f) => <Factura key={f.id} f={f} acciones={acciones(f)} />)}
        </ul>
      )}
      {ventanas}
    </div>
  );
}

function Hoja({ titulo, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card">
          <h3 className="font-heading text-base font-semibold">{titulo}</h3>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-muted"><XIcon size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inp = "w-full min-h-[44px] bg-app border border-border rounded-md px-3 py-2 text-sm";

/** «Proyecto o subcuenta»: el Delegado solicita (queda pendiente); el Administrador corrige directamente con motivo. */
export function CambioProyectoModal({ factura, onClose, onDone }) {
  const [op, setOp] = useState(null);
  const [destino, setDestino] = useState("");
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  useEffect(() => { api.get(`/cobranza/facturas/${factura.id}/opciones`).then(({ data }) => setOp(data)).catch((e) => { toast.error(apiErr(e)); onClose(); }); }, [factura.id, onClose]);
  const enviar = async (e) => {
    e.preventDefault();
    if (enviando) return;
    setEnviando(true);
    try {
      const { data } = await api.post(`/cobranza/facturas/${factura.id}/cambio-proyecto`, { destino_id: destino === "__principal" ? null : destino, motivo });
      toast.success(data.estado === "aplicado" ? "Proyecto actualizado" : "Solicitud enviada: pendiente de aprobación del Administrador");
      onDone();
    } catch (er) { toast.error(apiErr(er)); } finally { setEnviando(false); }
  };
  const actualId = op?.cuenta_actual?.id || "__principal";
  return (
    <Hoja titulo="Proyecto o subcuenta" onClose={onClose} testid="cambio-proyecto">
      {!op ? <div className="p-6 text-sm text-text-muted">Cargando…</div> : (
        <form onSubmit={enviar} className="p-4 space-y-3 text-sm">
          <div className="bg-app/60 border border-border rounded-md p-3 text-xs">
            <div className="font-semibold text-sm">{op.factura.cliente_nombre}</div>
            <div className="text-text-muted">Factura <span className="font-mono">{op.factura.numero}</span> · saldo {fmtL(op.factura.saldo_pendiente)}</div>
            <div className="mt-1">Asignación actual: <b>{op.cuenta_actual.codigo ? `${op.cuenta_actual.codigo} · ` : ""}{op.cuenta_actual.nombre}</b></div>
          </div>
          <label className="block"><span className="block text-xs font-semibold text-text-secondary mb-1">Proyecto o subcuenta *</span>
            <select required value={destino} onChange={(e) => setDestino(e.target.value)} className={inp} data-testid="cambio-destino">
              <option value="">— Elige el destino —</option>
              {op.cuentas.map((c) => {
                const id = c.id || "__principal";
                return <option key={id} value={id} disabled={id === actualId}>{c.codigo ? `${c.codigo} · ` : ""}{c.nombre}{id === actualId ? " (actual)" : ""}</option>;
              })}
            </select>
            <span className="block text-[11px] text-text-muted mt-1">Solo cuentas de este cliente. La subcuenta NC tiene su propio flujo («Mover a subcuenta NC»).</span>
          </label>
          <label className="block"><span className="block text-xs font-semibold text-text-secondary mb-1">Motivo *</span>
            <textarea required minLength={3} rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} className={inp} data-testid="cambio-motivo" /></label>
          <p className="text-[11px] text-text-muted">Se conservan los pagos, documentos e historial de la factura; los saldos pasan de la cuenta de origen a la de destino sin duplicarse.
            {op.puede_aprobar ? " Como Administrador, el cambio se aplica al guardar." : " Queda pendiente hasta que el Administrador lo apruebe; mientras tanto la factura conserva su asignación."}</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="min-h-[44px] px-4 border border-border rounded-md">Cancelar</button>
            <button type="submit" disabled={enviando || !destino || motivo.trim().length < 3} data-testid="cambio-enviar"
              className="min-h-[44px] px-4 bg-agro-green text-black font-semibold rounded-md disabled:opacity-50">{enviando ? "Enviando…" : op.puede_aprobar ? "Aplicar cambio" : "Enviar solicitud"}</button>
          </div>
        </form>
      )}
    </Hoja>
  );
}

/** «Mover a subcuenta NC»: el Delegado solicita; el Administrador autoriza en el mismo formulario. */
export function MoverNcModal({ factura, onClose, onDone }) {
  const [op, setOp] = useState(null);
  const [monto, setMonto] = useState("");
  const [motivo, setMotivo] = useState("");
  const [archivo, setArchivo] = useState(null);       // {id, nombre} | {error}
  const [enviando, setEnviando] = useState(false);
  useEffect(() => {
    api.get(`/cobranza/facturas/${factura.id}/opciones`).then(({ data }) => { setOp(data); setMonto(String(data.factura.disponible.toFixed(2))); })
      .catch((e) => { toast.error(apiErr(e)); onClose(); });
  }, [factura.id, onClose]);
  const subir = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("factura_id", factura.id); fd.append("archivo", file);
    setArchivo({ nombre: file.name, subiendo: true });
    try { const { data } = await api.post("/cobranza/nc-traslados/adjuntos", fd); setArchivo({ id: data.id, nombre: data.nombre }); }
    catch (e) { setArchivo({ nombre: file.name, error: apiErr(e) }); }
  };
  const m = Number(monto);
  const disp = op?.factura?.disponible || 0;
  const valido = m > 0 && m <= disp + 0.004 && motivo.trim().length >= 3 && !archivo?.subiendo;
  const enviar = async (e) => {
    e.preventDefault();
    if (enviando || !valido) return;
    setEnviando(true);
    try {
      const { data } = await api.post("/cobranza/nc-traslados", { factura_id: factura.id, monto: m, motivo, adjunto_id: archivo?.id || null });
      toast.success(data.estado === "aprobado_pendiente_generar" ? "Traslado autorizado: pendiente de generar la nota de crédito" : "Solicitud enviada: pendiente de aprobación");
      onDone();
    } catch (er) { toast.error(apiErr(er)); } finally { setEnviando(false); }
  };
  return (
    <Hoja titulo="Mover a subcuenta NC" onClose={onClose} testid="mover-nc">
      {!op ? <div className="p-6 text-sm text-text-muted">Cargando…</div> : (
        <form onSubmit={enviar} className="p-4 space-y-3 text-sm">
          <div className="bg-app/60 border border-border rounded-md p-3 text-xs grid grid-cols-2 gap-x-3 gap-y-1">
            <span className="text-text-muted">Cliente</span><b>{op.factura.cliente_nombre}</b>
            <span className="text-text-muted">Factura</span><span className="font-mono">{op.factura.numero}</span>
            <span className="text-text-muted">Cuenta de origen</span><span>{op.cuenta_actual.codigo ? `${op.cuenta_actual.codigo} · ` : ""}{op.cuenta_actual.nombre}</span>
            <span className="text-text-muted">Saldo disponible</span><b className="text-agro-yellow" data-testid="nc-disponible">{fmtL(disp)}</b>
            <span className="text-text-muted">Destino</span><span>Subcuenta NC del mismo cliente</span>
          </div>
          <label className="block"><span className="block text-xs font-semibold text-text-secondary mb-1">Importe a trasladar *</span>
            <input type="number" inputMode="decimal" step="0.01" min="0.01" max={disp} value={monto} onChange={(e) => setMonto(e.target.value)} className={inp}
              aria-invalid={!(m > 0 && m <= disp + 0.004)} data-testid="nc-monto" />
            <span className="block text-[11px] text-text-muted mt-1">Por defecto el saldo completo; puede ser parcial (mayor que cero y hasta el saldo disponible).</span></label>
          <label className="block"><span className="block text-xs font-semibold text-text-secondary mb-1">Motivo *</span>
            <textarea required minLength={3} rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} className={inp} data-testid="nc-motivo" /></label>
          <div>
            <span className="block text-xs font-semibold text-text-secondary mb-1">Respaldo (opcional)</span>
            <label className="min-h-[44px] px-3 border border-dashed border-border rounded-md text-xs flex items-center gap-2 cursor-pointer">
              <PaperclipIcon size={14} /> {archivo ? archivo.nombre : "Adjuntar PDF, imagen o documento (máx. 10 MB)"}
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx,image/*" className="hidden" onChange={(e) => subir(e.target.files?.[0])} data-testid="nc-archivo" />
            </label>
            {archivo?.subiendo && <p className="text-[11px] text-text-muted mt-1">Subiendo…</p>}
            {archivo?.error && <p className="text-[11px] text-red-300 mt-1">{archivo.error}</p>}
            {archivo?.id && <p className="text-[11px] text-agro-green mt-1 flex items-center gap-1"><CheckCircleIcon size={12} /> Adjuntado</p>}
          </div>
          <p className="text-[11px] text-text-muted">Trasladar a NC no emite una nota de crédito ni registra un pago: clasifica el saldo para su proceso.
            {op.puede_aprobar ? " Como Administrador, el traslado queda autorizado al guardar (pendiente de generar la nota de crédito)." : " Queda pendiente de aprobación del Administrador; mientras tanto el saldo permanece en su cuenta."}</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="min-h-[44px] px-4 border border-border rounded-md">Cancelar</button>
            <button type="submit" disabled={enviando || !valido} data-testid="nc-enviar"
              className="min-h-[44px] px-4 bg-agro-green text-black font-semibold rounded-md disabled:opacity-50">{enviando ? "Enviando…" : op.puede_aprobar ? "Autorizar traslado" : "Enviar solicitud"}</button>
          </div>
        </form>
      )}
    </Hoja>
  );
}
